import React from 'react';
import { Box, CircularProgress, Grid, Stack, Typography, useMediaQuery } from '@mui/material';
import IconButton from '../../../components/IconButton';
import { ICON_BUTTON_TYPE } from '../../../constants';
import TopBar from './TopBar';
import AddDiagnosisModal from './AddDiagnosisModal';
import { deleteDiagnosisApi, getDiagnosisApi, getUserDiagnosisApi, deleteUserDiagnosisApi } from '../../../utils/api';
import { useMutation, useQuery } from 'react-query';
import { isEmpty } from 'lodash';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

const Diagnoses = ({ user, recipient }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showAddModal, setShowAddModal] = React.useState(false);
  const [activeEditData, setActiveEditData] = React.useState(null);

  const { data, isLoading, refetch } = useQuery(
    ['getDiagnosisApi'],
    () =>
      recipient?._id
        ? getDiagnosisApi({ userId: user?._id, recipientId: recipient?._id })
        : getUserDiagnosisApi({ userId: user?._id }),
    {
      retry: false,
      cacheTime: 0,
    },
  );

  const deleteDiagnosisHandler = useMutation(recipient?._id ? deleteDiagnosisApi : deleteUserDiagnosisApi, {
    onSuccess: async ({ data }) => {
      refetch();
    },
    onError: (error) => {
      console.log('error =====> ', error);
    },
  });

  const handleDelete = (item) => {
    deleteDiagnosisHandler.mutate({ userId: user?._id, recipientId: recipient?._id, diagnosisId: item?._id });
  };

  const handleShowEdit = (item) => {
    setActiveEditData(item);
    setShowAddModal(true);
  };

  return (
    <Grid sx={{ marginBottom: '30px' }}>
      <TopBar btnText={t('addDiagnosis')} onClick={() => setShowAddModal(true)} />

      {isLoading && (
        <Grid marginY={10} container justifyContent={'center'}>
          <CircularProgress size={40} style={{ color: '#26786a' }} />
        </Grid>
      )}

      {!isLoading && isEmpty(data?.data) && (
        <Typography variant="h5" textAlign="center">
          {t('noDiagnosisFound')}
        </Typography>
      )}

      {!isLoading &&
        !isEmpty(data?.data) &&
        data?.data.map((item, idx) => (
          <Box
            key={idx}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              p: 3,
              mt: 2,
              bgcolor: 'background.paper',
              borderRadius: 2,
              '@media (max-width: 600px)': {
                width: '90%',
                mt: 2,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                  color: 'text.primary',
                  '@media (max-width: 600px)': {
                    fontSize: '1.5rem',
                  },
                }}
              >
                {item?.title}
              </Typography>

              <Stack
                direction="row"
                spacing={2}
                sx={{
                  '@media (max-width: 600px)': {},
                }}
              >
                <div style={{ display: 'flex', gap: 10 }}>
                  <IconButton type={ICON_BUTTON_TYPE.edit} onClick={() => handleShowEdit(item)} />
                  <IconButton type={ICON_BUTTON_TYPE.delete} onClick={() => handleDelete(item)} />
                </div>
              </Stack>
            </Box>

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                '@media (max-width: 600px)': {
                  fontSize: '0.875rem',
                },
              }}
            >
              {item?.description}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  '@media (max-width: 600px)': {
                    fontSize: '0.875rem',
                  },
                }}
              >
                {t('dateOfDiagnosis')}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: 'text.primary',
                  '@media (max-width: 600px)': {
                    fontSize: '0.875rem',
                  },
                }}
              >
                {item?.date}
              </Typography>
            </Box>
          </Box>
        ))}

      <AddDiagnosisModal
        show={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setActiveEditData(null);
        }}
        recipient={recipient}
        refetch={refetch}
        activeEditData={activeEditData}
      />
    </Grid>
  );
};

export default Diagnoses;
